import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, FileText, Download, Cloud, Sun, CloudRain, CloudSnow, Camera, X, Trash2, Check, Clock, CheckCircle2, XCircle, Hammer, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = {
  cantiere_id: "",
  data: new Date().toISOString().slice(0, 10),
  attivita: "",
  meteo: "sereno",
  operai_presenti: [],
  materiali: "",
  mezzi: "",
  note: "",
  foto: [],
  lavorazioni: [], // [{voce_id, percentuale, note}]
};
const meteoIcon = { sereno: Sun, nuvoloso: Cloud, pioggia: CloudRain, neve: CloudSnow };

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      const b64 = typeof r === "string" ? r.split(",")[1] : "";
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const statusMeta = {
  pending: { label: "In attesa approvazione", color: "bg-amber-100 text-amber-900 border-amber-300", icon: Clock },
  approvata: { label: "Approvato", color: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: CheckCircle2 },
  rifiutata: { label: "Rifiutato", color: "bg-red-100 text-red-900 border-red-300", icon: XCircle },
};

export default function Rendiconti() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [list, setList] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [operaiText, setOperaiText] = useState("");
  const [viewFoto, setViewFoto] = useState(null);
  const [vociPerCantiere, setVociPerCantiere] = useState({ cantiere_id: null, voci: [] });
  const [vociBusy, setVociBusy] = useState(false);
  const fotoInput = useRef();

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/rendiconti"), api.get("/cantieri")]);
    setList(r.data);
    setCantieri(c.data);
  };
  useEffect(() => { load(); }, []);

  // Carica voci quando il cantiere cambia
  useEffect(() => {
    const cid = form.cantiere_id;
    if (!cid) { setVociPerCantiere({ cantiere_id: null, voci: [] }); return; }
    setVociBusy(true);
    api.get(`/cantieri/${cid}/avanzamento-opere`)
      .then(({ data }) => setVociPerCantiere({ cantiere_id: cid, voci: data.voci || [] }))
      .catch(() => setVociPerCantiere({ cantiere_id: cid, voci: [] }))
      .finally(() => setVociBusy(false));
  }, [form.cantiere_id]);

  const resetForm = () => {
    setForm({
      cantiere_id: "",
      data: new Date().toISOString().slice(0, 10),
      attivita: "",
      meteo: "sereno",
      operai_presenti: [],
      materiali: "",
      mezzi: "",
      note: "",
      foto: [],
      lavorazioni: [],
    });
    setOperaiText("");
    setVociPerCantiere({ cantiere_id: null, voci: [] });
    setEditId(null);
  };

  const openEdit = (r) => {
    if (!isAdmin) return;
    setEditId(r.id);
    setForm({
      cantiere_id: r.cantiere_id || "",
      data: r.data || new Date().toISOString().slice(0, 10),
      attivita: r.attivita || "",
      meteo: r.meteo || "sereno",
      operai_presenti: r.operai_presenti || [],
      materiali: r.materiali || "",
      mezzi: r.mezzi || "",
      note: r.note || "",
      foto: r.foto || [],
      lavorazioni: r.lavorazioni || [],
    });
    setOperaiText((r.operai_presenti || []).join(", "));
    setOpen(true);
  };

  const addLavorazione = () => {
    const n = (form.lavorazioni || []).length;
    if (n >= 10) {
      toast.error("Massimo 10 lavorazioni per rendiconto");
      return;
    }
    setForm((f) => ({ ...f, lavorazioni: [...(f.lavorazioni || []), { voce_id: "", percentuale: 0, note: "" }] }));
  };
  const updateLav = (idx, patch) => {
    setForm((f) => ({ ...f, lavorazioni: f.lavorazioni.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  };
  const removeLav = (idx) => {
    setForm((f) => ({ ...f, lavorazioni: f.lavorazioni.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.cantiere_id) { toast.error("Seleziona il cantiere"); return; }
    if (!form.attivita?.trim()) { toast.error("Scrivi le attività svolte"); return; }
    const operai = operaiText.split(",").map((s) => s.trim()).filter(Boolean);
    // Cap ogni lavorazione a (100 - già fatto) per quella voce
    const lav = (form.lavorazioni || [])
      .filter((l) => l.voce_id)
      .slice(0, 10)
      .map((l) => {
        const v = vociMap[l.voce_id];
        const remaining = v ? Math.max(0, 100 - Number(v.percentuale || 0)) : 100;
        const raw = Math.max(0, Math.min(100, Number(l.percentuale) || 0));
        const pct = Math.min(raw, remaining);
        return { voce_id: l.voce_id, percentuale: pct, note: (l.note || "") };
      });
    try {
      const payload = { ...form, operai_presenti: operai, lavorazioni: lav };
      if (editId) {
        await api.put(`/rendiconti/${editId}`, payload);
        toast.success("Rendiconto aggiornato");
      } else {
        await api.post("/rendiconti", payload);
        toast.success(isAdmin ? "Rendiconto salvato e applicato" : "Rendiconto inviato, in attesa di approvazione");
      }
      resetForm(); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  const approva = async (rid) => {
    try { await api.post(`/rendiconti/${rid}/approva`); toast.success("Approvato e applicato alle lavorazioni"); load(); }
    catch { toast.error("Errore"); }
  };
  const rifiuta = async (rid) => {
    if (!window.confirm("Rifiutare questo rendiconto?")) return;
    try { await api.post(`/rendiconti/${rid}/rifiuta`); toast.success("Rifiutato"); load(); }
    catch { toast.error("Errore"); }
  };
  const remove = async (rid) => {
    if (!window.confirm("Eliminare il rendiconto?")) return;
    try { await api.delete(`/rendiconti/${rid}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const addPhotos = async (files) => {
    if (!files?.length) return;
    const arr = [];
    for (const f of files) {
      try { arr.push(await fileToBase64(f)); } catch {}
    }
    setForm((prev) => ({ ...prev, foto: [...(prev.foto || []), ...arr] }));
    toast.success(`${arr.length} foto aggiunta/e`);
  };
  const removePhoto = (idx) => setForm((prev) => ({ ...prev, foto: prev.foto.filter((_, i) => i !== idx) }));

  const cmap = Object.fromEntries(cantieri.map((c) => [c.id, c.nome]));
  const vociMap = useMemo(() => Object.fromEntries((vociPerCantiere.voci || []).map((v) => [v.id, v])), [vociPerCantiere]);

  const pending = useMemo(() => list.filter((r) => r.approvazione === "pending"), [list]);
  const altri = useMemo(() => list.filter((r) => r.approvazione !== "pending"), [list]);

  const renderCard = (r) => {
    const Icon = meteoIcon[r.meteo] || Cloud;
    const sm = statusMeta[r.approvazione || "approvata"];
    const StatusIcon = sm.icon;
    const mapId2Voce = Object.fromEntries((r._voci_snapshot || []).map((v) => [v.id, v]));
    return (
      <Card key={r.id} className="card-elevated">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <FileText className="h-4 w-4 text-primary" />
                <div className="font-bold truncate">{cmap[r.cantiere_id] || "Cantiere"}</div>
                <Badge variant="secondary" className="badge-tag">{r.data}</Badge>
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-semibold ${sm.color}`}>
                  <StatusIcon className="h-3 w-3" /> {sm.label}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                di <strong>{r.autore_nome}</strong>{r.autore_ruolo ? ` (${r.autore_ruolo})` : ""}
              </div>
            </div>
            <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
          </div>

          <div className="text-sm whitespace-pre-line">{r.attivita}</div>

          {Array.isArray(r.lavorazioni) && r.lavorazioni.length > 0 && (
            <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
                <Hammer className="h-3 w-3" /> Lavorazioni ({r.lavorazioni.length})
              </div>
              <div className="space-y-2">
                {r.lavorazioni.map((l, i) => (
                  <div key={i} className="text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate flex-1">
                        {(mapId2Voce[l.voce_id]?.descrizione) || l.descrizione || "Voce"}
                      </div>
                      <span className="font-bold tabular-nums text-primary shrink-0">{Number(l.percentuale || 0).toFixed(0)}%</span>
                    </div>
                    <Progress value={Number(l.percentuale || 0)} className="h-1.5 mt-1" />
                    {l.note && <div className="text-muted-foreground mt-0.5 italic">{l.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {r.operai_presenti?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {r.operai_presenti.map((o, i) => <Badge key={i} variant="outline" className="text-xs">{o}</Badge>)}
            </div>
          )}

          {r.foto?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {r.foto.map((f, i) => (
                <button key={i} onClick={() => setViewFoto(f)}>
                  <img src={`data:image/jpeg;base64,${f}`} alt={`foto-${i}`} className="h-16 w-16 object-cover rounded hover:ring-2 ring-primary" />
                </button>
              ))}
            </div>
          )}

          {(r.materiali || r.mezzi || r.note) && (
            <div className="text-xs text-muted-foreground space-y-1">
              {r.materiali && <div><strong>Materiali:</strong> {r.materiali}</div>}
              {r.mezzi && <div><strong>Mezzi:</strong> {r.mezzi}</div>}
              {r.note && <div><strong>Note:</strong> {r.note}</div>}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
            {isAdmin && r.approvazione === "pending" && (
              <>
                <Button data-testid={`approva-${r.id}`} size="sm" onClick={() => approva(r.id)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Check className="h-3 w-3 mr-1" /> Approva e applica
                </Button>
                <Button data-testid={`rifiuta-${r.id}`} size="sm" variant="outline" onClick={() => rifiuta(r.id)}>
                  <X className="h-3 w-3 mr-1" /> Rifiuta
                </Button>
              </>
            )}
            <a href={`${API}/pdf/rendiconto/${r.id}`} target="_blank" rel="noreferrer" data-testid={`pdf-rendiconto-${r.id}`} className="flex-1 min-w-[120px]">
              <Button variant="outline" size="sm" className="w-full"><Download className="h-3 w-3 mr-2" /> PDF</Button>
            </a>
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-rendiconto-${r.id}`} className="text-primary">
                <Pencil className="h-3 w-3" />
              </Button>
            )}
            {(isAdmin || (r.autore_id === user?.id && r.approvazione !== "approvata")) && (
              <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="text-destructive">
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Giornaliero</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Rendiconti</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isAdmin
              ? "Approva i rendiconti dei dipendenti: la % svolta oggi si somma a quella già fatta sulla voce (max 100)."
              : "Segna fino a 10 lavorazioni con la % svolta oggi (il resto per completarle apparirà in automatico sul prossimo rendiconto)."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-rendiconto" className="h-11"><Plus className="h-4 w-4 mr-2" /> Nuovo rendiconto</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Modifica rendiconto" : "Nuovo rendiconto giornaliero"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cantiere</Label>
                <Select value={form.cantiere_id} onValueChange={(v) => setForm({ ...form, cantiere_id: v, lavorazioni: [] })}>
                  <SelectTrigger data-testid="r-cantiere"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
              <div>
                <Label>Meteo</Label>
                <Select value={form.meteo} onValueChange={(v) => setForm({ ...form, meteo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sereno">Sereno</SelectItem>
                    <SelectItem value="nuvoloso">Nuvoloso</SelectItem>
                    <SelectItem value="pioggia">Pioggia</SelectItem>
                    <SelectItem value="neve">Neve</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Operai presenti (nomi separati da virgola)</Label><Input value={operaiText} onChange={(e) => setOperaiText(e.target.value)} /></div>
              <div className="col-span-2"><Label>Attività svolte</Label><Textarea rows={3} data-testid="r-attivita" value={form.attivita} onChange={(e) => setForm({ ...form, attivita: e.target.value })} /></div>

              {/* Lavorazioni con % */}
              <div className="col-span-2 border rounded-lg p-3 space-y-3 bg-secondary/30">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-bold flex items-center gap-1.5"><Hammer className="h-4 w-4" /> Lavorazioni eseguite</Label>
                    <div className="text-[11px] text-muted-foreground">
                      Max 10 righe. Indica <strong>quanto hai fatto oggi</strong> su ogni voce. Se la voce era al 50%, oggi puoi aggiungere al massimo il 50%.
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="add-lavorazione"
                    disabled={!form.cantiere_id || (form.lavorazioni || []).length >= 10}
                    onClick={addLavorazione}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Aggiungi ({(form.lavorazioni || []).length}/10)
                  </Button>
                </div>

                {!form.cantiere_id && (
                  <div className="text-xs text-muted-foreground italic">Scegli prima il cantiere per vedere la lista delle lavorazioni.</div>
                )}
                {form.cantiere_id && vociBusy && (
                  <div className="text-xs text-muted-foreground italic">Carico le lavorazioni del cantiere...</div>
                )}
                {form.cantiere_id && !vociBusy && vociPerCantiere.voci.length === 0 && (
                  <div className="text-xs text-muted-foreground italic">
                    Nessuna voce di computo per questo cantiere. Chiedi all'admin di caricare il computo metrico.
                  </div>
                )}

                {(form.lavorazioni || []).map((l, i) => {
                  const v = vociMap[l.voce_id];
                  const already = v ? Number(v.percentuale || 0) : 0;
                  const maxOggi = Math.max(0, 100 - already);
                  const curVal = Number(l.percentuale) || 0;
                  const overflow = curVal > maxOggi;
                  return (
                    <div key={i} className="rounded border border-border bg-card p-2 space-y-2" data-testid={`lav-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <Select value={l.voce_id} onValueChange={(vv) => updateLav(i, { voce_id: vv, percentuale: 0 })}>
                            <SelectTrigger data-testid={`lav-voce-${i}`} className="h-9"><SelectValue placeholder="Scegli lavorazione" /></SelectTrigger>
                            <SelectContent>
                              {vociPerCantiere.voci.map((vv) => (
                                <SelectItem key={vv.id} value={vv.id}>
                                  {vv.descrizione} {vv.extra ? "(extra)" : ""} — già {Number(vv.percentuale || 0).toFixed(0)}%
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeLav(i)}>
                          <X className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs w-32 shrink-0">% svolta oggi</Label>
                        <Input
                          data-testid={`lav-pct-${i}`}
                          type="number" min="0" max={maxOggi} step="1"
                          value={l.percentuale}
                          onChange={(e) => updateLav(i, { percentuale: e.target.value })}
                          className={`h-8 w-24 ${overflow ? "border-destructive" : ""}`}
                        />
                        <Progress value={Math.min(100, already + curVal)} className="h-2 flex-1" />
                      </div>
                      {v && (
                        <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                          <span>Già fatto: <strong>{already.toFixed(0)}%</strong></span>
                          <span>·</span>
                          <span>Massimo oggi: <strong className={overflow ? "text-destructive" : "text-primary"}>{maxOggi}%</strong></span>
                          {overflow && <span className="text-destructive font-semibold">· Verrà troncato a {maxOggi}%</span>}
                        </div>
                      )}
                      <Input
                        data-testid={`lav-note-${i}`}
                        placeholder="Note (opzionale)"
                        value={l.note}
                        onChange={(e) => updateLav(i, { note: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </div>
                  );
                })}
              </div>

              <div><Label>Materiali</Label><Textarea rows={2} value={form.materiali} onChange={(e) => setForm({ ...form, materiali: e.target.value })} /></div>
              <div><Label>Mezzi</Label><Textarea rows={2} value={form.mezzi} onChange={(e) => setForm({ ...form, mezzi: e.target.value })} /></div>
              <div className="col-span-2"><Label>Note aggiuntive</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              <div className="col-span-2 space-y-2">
                <Label>Foto cantiere</Label>
                <input ref={fotoInput} type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} data-testid="r-foto-input" />
                <Button type="button" variant="outline" onClick={() => fotoInput.current?.click()} data-testid="r-foto-btn">
                  <Camera className="h-4 w-4 mr-2" /> Aggiungi foto ({form.foto?.length || 0})
                </Button>
                {form.foto?.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {form.foto.map((f, i) => (
                      <div key={i} className="relative">
                        <img src={`data:image/jpeg;base64,${f}`} alt={`foto-${i}`} className="h-20 w-20 object-cover rounded" />
                        <button type="button" onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-5 w-5 flex items-center justify-center">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <Button data-testid="r-save" onClick={save} className="h-11 mt-2">
              {editId ? "Aggiorna rendiconto" : (isAdmin ? "Salva (viene applicato subito)" : "Invia per approvazione")}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      {isAdmin && pending.length > 0 ? (
        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Da approvare <Badge className="ml-2 bg-amber-500 hover:bg-amber-600 text-white">{pending.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="altri" data-testid="tab-altri">Archivio ({altri.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="pt-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {pending.map(renderCard)}
            </div>
          </TabsContent>
          <TabsContent value="altri" className="pt-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {altri.map(renderCard)}
              {altri.length === 0 && <div className="text-muted-foreground col-span-full py-12 text-center">Nessun rendiconto storico.</div>}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {list.map(renderCard)}
          {list.length === 0 && <div className="text-muted-foreground col-span-full py-12 text-center">Nessun rendiconto.</div>}
        </div>
      )}

      <Dialog open={!!viewFoto} onOpenChange={(o) => !o && setViewFoto(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Foto cantiere</DialogTitle></DialogHeader>
          {viewFoto && <img src={`data:image/jpeg;base64,${viewFoto}`} alt="foto" className="w-full rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
